import { useMutation, useQueryClient } from '@tanstack/react-query';
import { VisibilityEnum } from 'components/marketplace-listing/listing-form-schema';
import NotifyNetworkDialog from 'components/notify-network-dialog/notify-network-dialog';
import {
  Button,
  Dialog,
  ErrorMessage,
  Icon,
  RadioBox,
  Stack,
  Text,
} from 'components/ui';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import { useOrgUsers } from 'hooks/use-org-users';
import { ApiClient } from 'lib/api/client';
import {
  VisibilityEnum as ApiVisibilityEnum,
  ListingPublishAction,
  ListingRead,
  PublicStatusEnum,
} from 'lib/api/generated';
import {
  getHeadNetworks,
  getListOfNetworksId,
  getMyNetworks,
} from 'lib/marketplace-utils';
import { useStore } from 'lib/store';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { cattleQuestions } from 'settings/listing-questions/cattle-questions';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';

interface IForm {
  publish_action: ApiVisibilityEnum;
  networks: string[];
}

interface UpdateListingVisibilityDialogProps {
  isOpen: boolean;
  onClose: VoidFunction;
  listingDetails: ListingRead;
}

const GridWrapper = styled.div`
  display: grid;
  gap: ${globalTheme.space[1]};
  align-items: center;
  justify-content: center;
  grid-template-columns: 1fr 1fr;
  max-width: 300px;
`;

const PublicInfo = styled.div`
  background-color: ${globalTheme.colors.lightPink};
  color: ${globalTheme.colors.pink};
  border-radius: 20px;
  padding: ${globalTheme.space[1]} ${globalTheme.space[1]};
  display: flex;
  flex-direction: column;
  gap: ${globalTheme.space[0]};
  align-items: center;
`;

const FlexWrapper = styled.div`
  display: flex;
  gap: ${globalTheme.space[1]};
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const FormWrapper = styled(Stack)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

function UpdateListingVisibilityDialog({
  isOpen,
  onClose,
  listingDetails,
}: UpdateListingVisibilityDialogProps) {
  const queryClient = useQueryClient();
  const activeBuyerOrgId = useStore((store) => store.activeBuyerOrgId);
  const [
    notifyNetworkDialogOpen,
    openNotifyNetworkDialog,
    closeNotifyNetworkDialog,
  ] = useDialogToggle(false);

  // Create mutation
  const createMutation = useMutation(
    (listingPublishActionRequest: ListingPublishAction) =>
      new ApiClient().listingApi.orgMarketplaceListingPublishCreate({
        buyerOrgId: activeBuyerOrgId || '',
        listingId: listingDetails.id,
        listingPublishActionRequest,
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries([listingDetails.id]);
      },
      onSuccess: () => {
        toast.success('Listing updated successfully');
      },
      onError: () => {
        toast.error('Unable to update listing');
      },
    }
  );

  const methods = useForm({
    defaultValues: {
      publish_action:
        listingDetails.public_status === PublicStatusEnum.Draft
          ? ApiVisibilityEnum.Private
          : ApiVisibilityEnum.Public,
      networks: getListOfNetworksId(listingDetails.networks),
    },
  });

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
    getValues,
  } = methods;

  const { data: orgUser, isLoading } = useOrgUsers({ status: 'APPROVED' });
  const publishAction = useWatch({ control, name: 'publish_action' });

  const onSubmit = async (e: any) => {
    await createMutation.mutateAsync(e);
    if (e?.networks && e?.networks.length) {
      openNotifyNetworkDialog();
    } else {
      onClose();
    }
  };

  useEffect(() => {
    reset({
      publish_action:
        listingDetails.public_status === PublicStatusEnum.Draft
          ? ApiVisibilityEnum.Private
          : ApiVisibilityEnum.Public,
      networks: getListOfNetworksId(listingDetails.networks),
    });
  }, [isOpen, listingDetails]);

  const selectedNetworks = useMemo(() => {
    if (!listingDetails) return [];
    if (isLoading || !orgUser) return [];
    const selectedNetwork = orgUser.results?.find(
      (user) => user.user?.user_id === listingDetails?.creator?.user_id
    )?.networks;
    return selectedNetwork;
  }, [listingDetails, orgUser, isLoading]);

  return (
    <>
      <Dialog
        title='Update'
        isOpen={isOpen}
        onDismiss={onClose}
        actions={
          <>
            <Button
              variant='outline'
              size='small'
              type='button'
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              form='updateListingVisibility'
              size='small'
              type='submit'
              isLoading={createMutation.isLoading}
            >
              Update
            </Button>
          </>
        }
      >
        <FormProvider {...methods}>
          <form id='updateListingVisibility' onSubmit={handleSubmit(onSubmit)}>
            <FormWrapper space={1}>
              {/* Visibility */}
              <>
                <Text
                  className='header-text'
                  fontWeight='bold'
                  aria-label={'visibility'}
                >
                  {cattleQuestions.publish_action}
                </Text>
                <GridWrapper>
                  {Object.entries(VisibilityEnum).map(([key, value], idx) => (
                    <RadioBox
                      key={key}
                      label={value}
                      value={key}
                      {...register('publish_action', {
                        shouldUnregister: true,
                      })}
                      data-cy={`visibility${value.replace(
                        /\s+|[,\/]/g,
                        ''
                      )}Radio`}
                    />
                  ))}
                  <Text fontSize={1}>
                    Listing is private and can be shared via it’s direct URL
                  </Text>
                  <Text fontSize={1}>
                    Listing will display on the public Agora Marketplace
                  </Text>
                </GridWrapper>
                {errors.publish_action && (
                  <ErrorMessage>{errors?.publish_action?.message}</ErrorMessage>
                )}
              </>

              {publishAction === 'PUBLIC' &&
                listingDetails.public_status !== PublicStatusEnum.Approved && (
                  <PublicInfo>
                    <Icon size={2} component='AlertIcon' />
                    <Text fontSize={2} fontWeight='bold'>
                      {' '}
                      Public Marketplace notice{' '}
                    </Text>
                    <Text>
                      Listing will be reviewed by Agora team for approval prior
                      to publishing on the Marketplace
                    </Text>
                  </PublicInfo>
                )}

              {/* My Networks */}
              {/*My Networks */}
              {selectedNetworks &&
                !!selectedNetworks.length &&
                !!getMyNetworks(selectedNetworks, activeBuyerOrgId as string)
                  .length && (
                  <FlexWrapper>
                    <Text
                      className='header-text'
                      fontWeight='bold'
                      fontSize={3}
                      aria-label={'my-networks'}
                    >
                      Networks you've created
                    </Text>
                    <FlexWrapper>
                      {getMyNetworks(
                        selectedNetworks,
                        activeBuyerOrgId as string
                      ).map((network, idx) => (
                        <RadioBox
                          key={idx}
                          checkbox
                          label={network.name}
                          value={network.id}
                          {...register('networks', {
                            shouldUnregister: true,
                          })}
                          data-cy={`networks${network.name.replace(
                            /\s+|[,\/]/g,
                            ''
                          )}Radio`}
                        />
                      ))}
                    </FlexWrapper>
                  </FlexWrapper>
                )}

              {/* Head Networks */}
              {selectedNetworks &&
                !!selectedNetworks.length &&
                !!getHeadNetworks(selectedNetworks).length && (
                  <FlexWrapper>
                    <Text
                      className='header-text'
                      fontWeight='bold'
                      fontSize={3}
                      aria-label={'visibility'}
                    >
                      Networks you've been added to
                    </Text>

                    {getHeadNetworks(selectedNetworks).map((network, idx) => (
                      <RadioBox
                        key={idx}
                        checkbox
                        label={network.name}
                        value={network.id}
                        {...register('networks', {
                          shouldUnregister: true,
                        })}
                        data-cy={`networks${network.name.replace(
                          /\s+|[,\/]/g,
                          ''
                        )}Radio`}
                      />
                    ))}
                  </FlexWrapper>
                )}

              {errors.networks && (
                <ErrorMessage>{errors?.networks?.message}</ErrorMessage>
              )}
            </FormWrapper>
          </form>
        </FormProvider>
      </Dialog>
      <NotifyNetworkDialog
        isOpen={notifyNetworkDialogOpen}
        onClose={() => {
          closeNotifyNetworkDialog();
          onClose();
        }}
        networks={getValues('networks') as string[]}
        listing={listingDetails}
      />
    </>
  );
}

export default UpdateListingVisibilityDialog;
